import { usePathname, useSearchParams } from "next/navigation";
import { stringify } from "qs";

import { normalizeUrlSearchParamsToRouterQuery } from "@/utils/normalizeUrlSearchParamsToRouterQuery";
import { buildQueryPath } from "@/utils/queryPath";
import sanitizePage from "@/utils/sanitizePage";

import useDomain from "./useDomain";

export default ({
  params,
  preserveParamOrder,
  encodeParams = { encode: false, indices: false },
}: {
  params?: string[];
  preserveParamOrder?: boolean;
  encodeParams?: Parameters<typeof stringify>[1];
}): string => {
  const domain = useDomain();
  const pathname = usePathname() || "";
  const searchParams = useSearchParams();
  const normalizedSearchParams =
    normalizeUrlSearchParamsToRouterQuery(searchParams);

  const asPath = buildQueryPath(pathname, normalizedSearchParams);
  const url = `${domain}${asPath}`;

  if (params) {
    try {
      const queryKeys = Object.keys(normalizedSearchParams);

      if (params.length && queryKeys.length) {
        let newQuery: Record<string, string | string[] | undefined> = {};

        if (preserveParamOrder) {
          params.forEach((param) => {
            if (normalizedSearchParams[param] !== undefined) {
              newQuery[param] = normalizedSearchParams[param];
            }
          });
        } else {
          newQuery = Object.fromEntries(
            Object.entries(normalizedSearchParams).map(([key, value]) => [
              key,
              String(value),
            ]),
          );
          queryKeys.forEach((key: string) => {
            if (!params.includes(key)) {
              delete newQuery[key];
            }
          });
        }

        if (newQuery && "page" in newQuery) {
          const pageNumber = Number(sanitizePage(newQuery.page as string));
          if (pageNumber && pageNumber > 1) {
            newQuery.page = `${pageNumber}`;
          } else {
            delete newQuery["page"];
          }
        }

        const queryString = stringify(newQuery, encodeParams);

        return `${domain}${pathname}${queryString ? `?${queryString}` : ""}`;
      } else {
        return `${domain}${pathname}`;
      }
    } catch {
      return "";
    }
  } else {
    return url;
  }
};
